import { Inject, Injectable, Optional } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, delay, map } from 'rxjs/operators';

import { KeystrokeReportsPort } from '../../../app/ports/reports/keystroke-reports.port';
import { KeystrokeReportRequest } from '../../../domain/models/reports/request/keystroke-report.request';
import { KeystrokeReportResponse } from '../../../domain/models/reports/keystroke-report.model';
import { CoreUIToastDirection, CoreUIToastV2Service } from '../../services/toast.service';
import { ReportSdk } from 'lib-klab-sdks';
import { EnvMicroReport } from '../../config/env.micro.report';
import { REPORT_ENV } from '../../config/micros-env.tokens';
import { mapSdkKeystrokeReportToDomain } from '../../mappers/reports/keystroke-reports.mapper';
import { keystrokeReportsSdkMock } from '../../mocks/report/keystroke-reports.mock';

type KeystrokeReportsMockAction = 'getEmployeeKeystrokes';

@Injectable({ providedIn: 'root' })
export class KeystrokeReportsPortAdapter implements KeystrokeReportsPort {
    constructor(
        private keystrokeReportsService: ReportSdk.KeystrokeReportsService,
        @Optional() private toast: CoreUIToastV2Service,
        @Inject(REPORT_ENV) private env: EnvMicroReport,
    ) { }

    getEmployeeKeystrokes(payload: KeystrokeReportRequest): Observable<KeystrokeReportResponse> {
        const source$: Observable<ReportSdk.KeystrokeReportResponse> = this.shouldMock('getEmployeeKeystrokes')
            ? of(
                keystrokeReportsSdkMock.getEmployeeKeystrokes(
                    payload.employeeId,
                    payload.dateRange.startDate,
                    payload.dateRange.endDate,
                    payload.page ?? 0,
                    payload.size ?? 10,
                    payload.search,
                ),
            ).pipe(delay(360))
            : this.keystrokeReportsService.getEmployeeKeystrokes({
                employeeId: payload.employeeId,
                startDate: payload.dateRange.startDate,
                endDate: payload.dateRange.endDate,
                page: payload.page ?? 0,
                size: payload.size ?? 10,
                search: payload.search?.trim() || undefined,
            });

        return source$.pipe(
            map((resp) => mapSdkKeystrokeReportToDomain(resp)),
            catchError((err) => {
                this.showErrorToast(err);
                return throwError(() => err);
            }),
        );
    }

    private shouldMock(action: KeystrokeReportsMockAction): boolean {
        const actionMock = this.env?.mock?.keystrokeReports?.[action];
        return actionMock ?? Boolean(this.env?.useMocks);
    }

    private showErrorToast(err: any): void {
        if (!this.toast) return;
        const status = err?.status;
        const mapMsg: Record<number, string> = {
            400: 'Solicitud inválida',
            401: 'No autorizado',
            404: 'No encontrado',
            429: 'Demasiadas solicitudes',
            500: 'Error interno del servidor',
        };

        this.toast.show({
            data: {
                title: 'Error',
                msg: mapMsg[status] || 'Error inesperado',
                direction: CoreUIToastDirection.topCenter,
                autoClose: true,
            },
        });
    }
}
